import { useEffect, useState } from "react";
import {
  Activity,
  Boxes,
  BrainCircuit,
  ChevronLeft,
  Cpu,
  Database,
  GitBranch,
  History,
  LayoutDashboard,
  type LucideIcon,
  MessagesSquare,
  Network,
  Rocket,
  Settings,
  Shield,
  ShoppingBag,
  Sparkles,
  Wrench,
  Zap,
  Plug,
} from "lucide-react";
import { cn } from "@/lib/utils";

type NavItem = {
  id: string;
  label: string;
  icon: LucideIcon;
  badge?: string;
  hot?: boolean;
};

const NAV_GROUPS: { title: string; items: NavItem[] }[] = [
  {
    title: "Command",
    items: [
      { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
      { id: "chat", label: "Universal Chat", icon: MessagesSquare, hot: true },
      { id: "canvas", label: "Agent Canvas 4D", icon: Network, badge: "LIVE" },
      { id: "agents", label: "Agents Hub", icon: BrainCircuit },
    ],
  },
  {
    title: "Intelligence",
    items: [
      { id: "tools", label: "Tools Galaxy", icon: Wrench, badge: "25" },
      { id: "memory", label: "Memory Universe", icon: Database },
      { id: "brain", label: "Second Brain", icon: Sparkles, hot: true },
      { id: "workflows", label: "Workflows Forge", icon: GitBranch },
      { id: "history", label: "History Timeline", icon: History },
    ],
  },
  {
    title: "Operations",
    items: [
      { id: "scheduler", label: "Scheduler", icon: Activity },
      { id: "sandbox", label: "Sandbox", icon: Shield },
      { id: "marketplace", label: "Marketplace", icon: ShoppingBag, badge: "6", hot: true },
      { id: "builder", label: "Builder Studio", icon: Wrench },
      { id: "integrations", label: "Integrations", icon: Plug, hot: true },
      { id: "governance", label: "Governance", icon: Shield },
      { id: "devops", label: "DevOps Studio", icon: Rocket },
    ],
  },
];

interface Props {
  active: string;
  onSelect: (id: string) => void;
  collapsed: boolean;
  onToggle: () => void;
}

export function NeuralSidebar({ active, onSelect, collapsed, onToggle }: Props) {
  const [pulse, setPulse] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setPulse((p) => (p + 1) % 100), 80);
    return () => clearInterval(id);
  }, []);

  return (
    <aside
      aria-label="Navegação principal"
      className={cn(
        "relative flex flex-col h-full bg-sidebar border-r border-sidebar-border transition-all duration-300",
        "max-md:absolute max-md:z-40 max-md:top-0 max-md:bottom-0 max-md:left-0 max-md:shadow-2xl",
        collapsed ? "w-[72px] max-md:-translate-x-full" : "w-[260px]",
      )}
    >
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 h-16 border-b border-sidebar-border">
        <div className="relative">
          <div className="w-9 h-9 rounded-lg bg-gradient-neural flex items-center justify-center shadow-glow-cyan">
            <BrainCircuit className="w-5 h-5 text-primary-foreground" />
          </div>
          <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-success shadow-[0_0_8px_currentColor] animate-pulse" />
        </div>
        {!collapsed && (
          <div className="flex-1 min-w-0 animate-slide-in-left">
            <div className="text-sm font-display font-semibold text-sidebar-foreground truncate">
              P2HM Agents
            </div>
            <div className="text-[10px] font-mono text-primary tracking-widest">v19 GOD MODE</div>
          </div>
        )}
        {!collapsed && (
          <button
            onClick={onToggle}
            className="p-1 rounded-md hover:bg-sidebar-accent text-muted-foreground"
            aria-label="Collapse"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-5">
        {NAV_GROUPS.map((group) => (
          <div key={group.title}>
            {!collapsed && (
              <div className="px-3 mb-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground/70">
                {group.title}
              </div>
            )}
            <div className="space-y-0.5">
              {group.items.map((item) => {
                const Icon = item.icon;
                const isActive = active === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => onSelect(item.id)}
                    className={cn(
                      "group w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-all relative",
                      isActive
                        ? "bg-sidebar-accent text-sidebar-accent-foreground shadow-[inset_0_0_0_1px_hsl(var(--primary)/0.4)]"
                        : "text-sidebar-foreground/80 hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground",
                    )}
                  >
                    {isActive && (
                      <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-primary rounded-r shadow-glow-cyan" />
                    )}
                    <Icon className={cn("w-4 h-4 shrink-0", isActive && "text-primary")} />
                    {!collapsed && (
                      <>
                        <span className="flex-1 text-left truncate">{item.label}</span>
                        {item.hot && (
                          <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-accent/20 text-accent border border-accent/30">
                            HOT
                          </span>
                        )}
                        {item.badge && (
                          <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-primary/15 text-primary border border-primary/30">
                            {item.badge}
                          </span>
                        )}
                      </>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Live Network Panel */}
      {!collapsed && (
        <div className="m-3 p-3 rounded-lg glass border-primary/20 animate-fade-in">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <Cpu className="w-3.5 h-3.5 text-primary" />
              <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                Agent Mesh
              </span>
            </div>
            <span className="text-[10px] font-mono text-success">● ONLINE</span>
          </div>
          <div className="space-y-1.5">
            {[
              { label: "Agents", value: "1,284", color: "primary" },
              { label: "Tokens/s", value: "42.7K", color: "secondary" },
              { label: "GPU", value: "78°C", color: "accent" },
            ].map((stat) => (
              <div key={stat.label} className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">{stat.label}</span>
                <span className="font-mono text-foreground">{stat.value}</span>
              </div>
            ))}
            {/* Activity bars */}
            <div className="flex items-end gap-0.5 h-8 mt-2">
              {Array.from({ length: 24 }).map((_, i) => {
                const h = ((Math.sin((pulse + i * 7) / 5) + 1) / 2) * 100;
                return (
                  <div
                    key={i}
                    className="flex-1 bg-gradient-to-t from-primary/40 to-secondary/80 rounded-sm transition-all"
                    style={{ height: `${20 + h * 0.8}%` }}
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="border-t border-sidebar-border p-2">
        <button
          onClick={() => onSelect("settings")}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-sidebar-foreground/80 hover:bg-sidebar-accent",
            active === "settings" && "bg-sidebar-accent text-sidebar-accent-foreground",
          )}
        >
          <Settings className="w-4 h-4 shrink-0" />
          {!collapsed && <span>Settings</span>}
        </button>
        {collapsed && (
          <button
            onClick={onToggle}
            className="mt-1 w-full flex justify-center p-2 rounded-md hover:bg-sidebar-accent text-muted-foreground"
            aria-label="Expand"
          >
            <Zap className="w-4 h-4" />
          </button>
        )}
      </div>
    </aside>
  );
}
